/**
 * Slug helpers for SEO routes. Keep slugs lowercase, hyphenated,
 * ASCII-only - Indian sector names with apostrophes / accents must be
 * normalized so URLs stay clean and don't double-encode in sitemaps.
 */

export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** "Back Pain Therapy" → "back-pain-therapy". Used to build service URL slugs. */
export const serviceSlug = slugify;

/** "Noida Sector 1" → "noida-sector-1". Used for city-sector URL segments. */
export const localitySlug = slugify;

/** Reverse: "back-pain-therapy" → "Back Pain Therapy" (Title Case). */
export function deslugify(slug: string): string {
  return slug
    .split('-')
    .map((w) => (w.length === 0 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * Split a hyphenated slug into a base slug and optional city suffix.
 *
 * Used to parse routes like `/conditions/back-pain-in-noida` and
 * `/recovery/acl-recovery-in-gurgaon` where React Router can't
 * directly extract multiple params from a single segment.
 *
 * Matches the LONGEST candidate citySlug from `validCitySlugs`. This
 * avoids ambiguity if a future city slug contains "in" as a substring.
 */
export function parseSlugWithCitySuffix(
  slug: string,
  validCitySlugs: string[],
): { base: string; citySlug?: string } {
  // Try each valid city slug to see if the input ends with "-in-<citySlug>"
  for (const c of validCitySlugs) {
    const suffix = `-in-${c}`;
    if (slug.endsWith(suffix)) {
      return { base: slug.slice(0, -suffix.length), citySlug: c };
    }
  }
  return { base: slug };
}

/**
 * Parse a `physiotherapy-in-...` slug or a `<service>-in-...` slug into
 * its components. Used for /physiotherapy-in-:rest and /:slug routes.
 *
 * Returns { citySlug, sectorSlug? } given the rest of the URL after
 * "physiotherapy-in-" or after the service prefix.
 *
 * Strategy: try to match the LONGEST suffix that matches a known sector
 * (so "noida-sector-18" → city=noida, sector=sector-18). Falls back to
 * the entire string as a city slug if no sector matches.
 */
export function parseCityAndSector(
  rest: string,
  citySlugs: string[],
  sectorIndex: { citySlug: string; slug: string }[],
): { citySlug?: string; sectorSlug?: string } {
  // Try to match "<city>-<sector>" by walking through known sectors
  for (const s of sectorIndex) {
    const candidate = `${s.citySlug}-${s.slug}`;
    if (rest === candidate) {
      return { citySlug: s.citySlug, sectorSlug: s.slug };
    }
  }
  // No sector - must be just a city slug
  if (citySlugs.includes(rest)) return { citySlug: rest };
  return {};
}

/**
 * Parse a `/:slug` route where `slug` is `<service>-in-<city>` or
 * `<service>-in-<city>-<sector>`.
 */
export function parseServiceCityAndSector(
  slug: string,
  citySlugs: string[],
  sectorIndex: { citySlug: string; slug: string }[],
): { serviceSlug?: string; citySlug?: string; sectorSlug?: string } {
  // Find "-in-" splitter; the service is everything before it
  const marker = '-in-';
  const idx = slug.indexOf(marker);
  if (idx === -1) return {};

  const serviceSlug = slug.slice(0, idx);
  const rest = slug.slice(idx + marker.length);
  if (!serviceSlug || !rest) return {};

  const cityAndSector = parseCityAndSector(rest, citySlugs, sectorIndex);
  return { serviceSlug, ...cityAndSector };
}
